import { startAuthentication } from '@simplewebauthn/browser'
import { useCurrentAdmin } from '@/composables/useCurrentAdmin'
import { t } from '@/i18n/t'
import { ApiError, api } from '@/utils/apiClient'
import { endOperatorSession } from '@/utils/endOperatorSession'

// Performs a fresh WebAuthn assertion to mint a new passkey_sudo cookie.
// Reuses the existing operator login endpoints (/api/admin/webauthn/login/*)
// — the backend's FinishPasskeyLogin sets BOTH the access cookies AND the
// passkey_sudo cookie, so a successful re-assertion against the same
// endpoints refreshes the sudo window without needing a dedicated route.
//
// Returns true on success, false on cancel / failure. The caller (the
// global passkey sudo prompter wired in App.vue) translates that into
// "retry the original request" vs "surface the original 403".

export interface PasskeySudoReauthResult {
  ok: boolean
  // Norwegian error copy for the modal to render inline. Empty when ok.
  error: string
}

// A `no_passkey` response during a sudo re-auth means the server has zero
// passkeys on file for this operator — the session can never satisfy a
// sudo gate again, so it's effectively dead. Rather than strand the
// operator in the re-auth modal, end the session and hard-redirect to
// login, where they re-bootstrap via magic link. Best-effort logout; we
// redirect regardless. The shared session-ending seam also clears the
// operator data that a full-page navigation alone would leave behind.
async function endDeadSession(): Promise<void> {
  await endOperatorSession()
  window.location.href = '/auth/login'
}

export function usePasskeySudoReauth() {
  const { fetchCurrentAdmin } = useCurrentAdmin()

  async function reauth(): Promise<PasskeySudoReauthResult> {
    const admin = await fetchCurrentAdmin()
    if (!admin?.email) {
      return { ok: false, error: t('composablesUsePasskeySudoReauth.accountNotFound') }
    }
    try {
      const beginRes = await api.post<
        Record<string, unknown> & { no_passkey?: boolean; stale_rp_id?: boolean }
      >('/api/admin/webauthn/login/begin', { email: admin.email })
      if (beginRes.no_passkey) {
        // Dead session — no factor left to elevate. Log out + bounce to
        // login instead of leaving the operator stuck in the modal.
        await endDeadSession()
        return {
          ok: false,
          error: t('composablesUsePasskeySudoReauth.noPasskey'),
        }
      }
      // Post-#1294 stale-RP-ID: every credential on file was minted
      // under a previous RP ID and can't satisfy a fresh assertion.
      // Sudo is unsatisfiable here — without this branch the
      // {stale_rp_id: true} body would feed startAuthentication() as
      // options (no `publicKey` key, no challenge), throw immediately,
      // and the modal would invite a retry loop until the rate
      // limiter 429s. Distinct terminal error instead.
      if (beginRes.stale_rp_id) {
        return {
          ok: false,
          error: t('composablesUsePasskeySudoReauth.staleRpId'),
        }
      }
      // Go's webauthn library wraps the options in `{publicKey: ...}`;
      // simplewebauthn expects the inner object. The login flow does the
      // same unwrap (see LoginView.vue).
      const optionsJSON = (beginRes.publicKey ?? beginRes) as Parameters<
        typeof startAuthentication
      >[0]['optionsJSON']
      const credential = await startAuthentication({ optionsJSON })
      await api.post('/api/admin/webauthn/login/finish', credential)
      return { ok: true, error: '' }
    } catch (e) {
      if (e instanceof Error && (e.name === 'NotAllowedError' || e.name === 'AbortError')) {
        return { ok: false, error: t('composablesUsePasskeySudoReauth.cancelled') }
      }
      if (e instanceof ApiError) {
        return { ok: false, error: t('composablesUsePasskeySudoReauth.authFailed') }
      }
      return { ok: false, error: t('composablesUsePasskeySudoReauth.genericError') }
    }
  }

  return { reauth }
}
